import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AppService, DcoLite, SearchUserSessionsRequest, SearchUserSessionsResponse, UserSession} from '../../../app.service';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../../../common/alert.service';
import { friendlyBrowser, uaTooltipText, friendlyOs, deviceLabel, browserIconClass, osIconClass, deviceIconClass } from '../../../common/ua-utils';
import { trigger, state, style, transition, animate } from '@angular/animations';


type Row = GroupRow | SessionRow;

interface GroupRow {
  kind: 'group';
  userId: string;
  name: string;
  email: string;
  dcoDisplay?: string | null;
  sessionsCount: number;
  lastSeenMax?: string | null;
}

interface SessionRow {
  kind: 'session';
  userId: string;
  session: UserSession;
}

@Component({
  selector: 'app-active-users-table',
  templateUrl: './active-users-table.component.html',
  styleUrls: ['./active-users-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('chev', [
    state('collapsed', style({ transform: 'rotate(0deg)' })),
    state('expanded', style({ transform: 'rotate(90deg)' })),
    transition('collapsed <=> expanded', animate('200ms ease'))
  ])]
})

export class ActiveUsersTableComponent implements OnInit {

  sessions: UserSession[] = [];

  currentSearchValue = '';
  currentFilterSelection: number | null = null;   // DCO id
  activeOnly = true;
  staleHours = 4;

  // date range (same toolbar look; defaults to Today)
  startDate: Date = new Date();
  endDate: Date = new Date();

  // data
  sessionsDataSource = new MatTableDataSource<UserSession>([]);

  displayedColumns: string[] = [
    'firstName', 'lastName', 'emailAddress',
    'dco', 'authSource', 'created', 'lastSeen',
    'browser', 'isActive'
  ];

  private sessionsByUser = new Map<string, UserSession[]>();

  hoveredRow: UserSession | null = null; // for the grouped popover

  // expose helpers used in template
  friendlyBrowser = friendlyBrowser;
  friendlyOs = friendlyOs;
  deviceLabel = deviceLabel;
  browserIconClass = browserIconClass;
  osIconClass = osIconClass;
  deviceIconClass = deviceIconClass;

  total = 0;
  showClosed = true; // button on right, mirroring existing toolbar

  groupByUser = false;
  rows: Row[] = [];
  expandedUserIds = new Set<string>();

  // paging/sorting
  pageSize = 25;
  pageIndex = 0;
  sortActive = 'createdUtc';
  sortDirection: 'asc' | 'desc' = 'desc';

  loading = false;

  dcos: DcoLite[] = [];

  private fmtLocal = new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: true,
    timeZone: environment.displayTimeZone ?? 'America/Los_Angeles', timeZoneName: 'short'
  });
  private fmtUtc = new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: true, timeZone: 'UTC', timeZoneName: 'short'
  });
  timezoneLocal = true;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private svc: AppService,
    private cdr: ChangeDetectorRef,
    private alert: AlertService) { }

  ngOnInit(): void {
    this.svc.GetDcosLite(true).subscribe(d => { this.dcos = d; this.cdr.markForCheck(); });
    this.load(); // initial
  }

  // --- toolbar actions (match User Management patterns) ---
  onSearch() { this.resetToFirstPageAndLoad(); }
  clearSearch() { this.currentSearchValue = ''; this.onSearch(); }
  onFilterChange() { this.resetToFirstPageAndLoad(); }
  toggleFilters() {  this.resetToFirstPageAndLoad(); }
  toggleShowClosed() { this.activeOnly = !this.activeOnly; this.resetToFirstPageAndLoad(); }

  // date helpers
  setToday() { const d = new Date(); this.startDate = d; this.endDate = d; this.resetToFirstPageAndLoad(); }
  setYesterday() {
    const y = new Date(); y.setDate(y.getDate() - 1);
    this.startDate = y; this.endDate = y; this.resetToFirstPageAndLoad();
  }
  setLast7() {
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - 6);
    this.startDate = start; this.endDate = end; this.resetToFirstPageAndLoad();
  }
  setThisMonth() {
    const now = new Date();
    this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.resetToFirstPageAndLoad();
  }

  // table events
  onSortChange(sort: Sort) {
    this.sortActive = this.mapSortField(sort.active);
    this.sortDirection = (sort.direction || 'asc') as any;
    this.load();
  }
  onPageChange(e: PageEvent) {
    this.pageIndex = e.pageIndex; this.pageSize = e.pageSize; this.load();
  }

  // formatting + icons
  formatDt(iso?: string | null) {
    if (!iso) return '';
    const d = new Date(iso);

    //formatted = formatted.replace(/(\D)24:/, '$100:');

    return this.timezoneLocal ? this.fmtLocal.format(d).replace(/(\D)24:/, '$100:') : this.fmtUtc.format(d).replace(/(\D)24:/, '$100:');
  }
  toggleTz() { this.timezoneLocal = !this.timezoneLocal; }

  isInternal(authSource?: string | null) {
    return authSource === 'entra';
  }

  getActiveIconClass(row: UserSession) {
    // Font Awesome consistency with other table
    return row.isTerminated ? 'fa-solid fa-circle-xmark inactive-icon' : 'fa-solid fa-circle-check active-icon';
  }

  // --- core load ---
  public resetToFirstPageAndLoad() { this.pageIndex = 0; this.load(); }

  private toUtcRangeForWholeDaysInZone(startLocal: Date, endLocal: Date, zone = (environment.displayTimeZone ?? 'America/Los_Angeles')) {
    const parts = (d: Date) =>
      new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d).split('-').map(Number);
    const mk = (y: number, m: number, d: number, h = 0, mi = 0, s = 0, ms = 0) => new Date(y, m, d, h, mi, s, ms);
    const [ys, ms, ds] = parts(startLocal);
    const [ye, me, de] = parts(endLocal);
    const start = mk(ys, ms - 1, ds, 0, 0, 0, 0);
    const end = mk(ye, me - 1, de, 23, 59, 59, 999);
    return { startUtc: start.toISOString(), endUtc: end.toISOString() };
  }

  private load() {
    const { startUtc, endUtc } = this.toUtcRangeForWholeDaysInZone(this.startDate, this.endDate);

    const req: SearchUserSessionsRequest = {
      startUtc, endUtc,
      dcoId: this.currentFilterSelection ?? undefined,
      userQuery: this.currentSearchValue?.trim() || undefined,
      activeOnly: this.activeOnly,
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      sort: this.sortActive,
      dir: this.sortDirection
    };

    this.loading = true;
    this.cdr.markForCheck();

    this.svc.SearchUserSessions(req).subscribe({
      next: (res: SearchUserSessionsResponse) => {
        if (res.success) {
          this.sessions = res.items ?? [];
          this.sessionsDataSource.data = res.items ?? [];
          this.total = res.total ?? 0;
        } else {
          // Business / API error
          const msg = res.message || 'Unknown error from server.';
          this.alert.error(`Error retrieving session log: ${msg}`);
          this.sessions = [];
          this.sessionsDataSource.data = [];
          this.total = 0;
        }

      },
      error: (err) => {
        // Transport / network error
        console.error('[ActiveUsers] HTTP error:', err);

        let msg = 'Unexpected error while loading sessions.';
        if (err.status === 0) {
          msg = 'Unable to reach the server. Please check your network.';
        } else if (err.status >= 500) {
          msg = 'Server error. Please try again later.';
        } else if (err.error?.message) {
          msg = err.error.message;
        }

        this.alert.error(msg);
        this.sessions = [];
        this.sessionsDataSource.data = [];
        this.total = 0;
      },
      complete: () => {
        this.loading = false;
        this.cdr.markForCheck();
        this.setData(this.sessions);
      }
    });
  }

  // stale = active but lastSeen > 4h (or missing)
  isStale(r: UserSession): boolean {
    if (r.isTerminated) return false;         // terminated is not "stale"; it's ended
    if (!r.lastSeenUtc) return true;
    const last = new Date(r.lastSeenUtc).getTime();
    return (Date.now() - last) > this.staleHours * 60 * 60 * 1000;
  }

  // inactive if terminated OR stale
  isInactive(r: UserSession): boolean {
    return r.isTerminated || this.isStale(r);
  }

  uaTooltip(row: any): string {
    return uaTooltipText(row);
  }

  toggleGroupView(checked: boolean, sessions: UserSession[]) {
    this.groupByUser = checked;
    this.expandedUserIds.clear();
    this.setData(sessions);
  }
  toggleUser(userId: string) {
    this.expandedUserIds.has(userId)
      ? this.expandedUserIds.delete(userId)
      : this.expandedUserIds.add(userId);
  }

  // Predicates for *matRowDef.when (must be function refs, not lambdas inline)
  groupRowPredicate = (index: number, row: any) =>
    this.groupByUser && row?.kind === 'group';

  sessionRowPredicate = (index: number, row: any) =>
    this.groupByUser && row?.kind === 'session';

  groupSummaryPredicate = (_: number, row: any) => this.groupByUser && row?.kind === 'group';
  groupDetailPredicate = (_: number, row: any) => this.groupByUser && row?.kind === 'group';
  flatRowPredicate = (_: number, _row: any) => !this.groupByUser;

  // Toggle handler (no template 'sessions' reference needed)
  onGroupToggle(checked: boolean) {
    this.groupByUser = checked;
    this.expandedUserIds.clear();
    this.setData(this.sessions);
  }

  // Build rows from sessions
  setData(sessions: UserSession[]) {
    this.sessions = sessions ?? [];
    this.rows = this.groupByUser ? this.toGroupedRows(this.sessions) : this.toFlatRows(this.sessions);
  }

  toFlatRows(sessions: UserSession[]): any[] /* Row[] */ {
    // in flat mode we can just pass sessions straight through if your template expects 'r'
    return sessions;
  }


  toGroupedRows(sessions: UserSession[]): any[] {
    this.sessionsByUser.clear();

    const byUser = new Map<string, UserSession[]>();
    for (const s of sessions) {
      const list = byUser.get(s.userId) ?? [];
      list.push(s);
      byUser.set(s.userId, list);
    }

    const out: any[] = [];
    for (const [userId, list] of byUser) {
      this.sessionsByUser.set(userId, list);

      const first = list[0];
      const name = `${first.firstName ?? ''} ${first.lastName ?? ''}`.trim() || first.email;
      const lastSeenMax = list
        .map(x => x.lastSeenUtc ?? x.createdUtc)
        .sort((a, b) => (a! > b! ? -1 : 1))[0];

      out.push({
        kind: 'group',
        userId,
        name,
        email: first.email,
        dcoDisplay: first.dcoDisplay,
        sessionsCount: list.length,
        lastSeenMax
      });
    }
    return out;
  }

  userSessions(userId: string): UserSession[] {
    return this.sessionsByUser.get(userId) ?? [];
  }

  // Initials for avatar (e.g., “Alice Anders” -> “AA”, else first two of email)
  initials(display: string): string {
    if (!display) return '?';
    const parts = display.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    const t = parts[0];
    return (t[0] + (t[1] || '')).toUpperCase();
  }

  // Aggregate per-user stats for summary row
  userAgg(userId: string): {
    active: number;
    total: number;
    browsers: Array<{ label: string; count: number }>;
  } {
    const list = this.sessionsByUser.get(userId) ?? [];
    const total = list.length;

    // active = !isTerminated && lastSeenUtc within X hours (reuse your staleHours)
    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() - this.staleHours);

    const active = list.filter(s => !s.isTerminated && (!!s.lastSeenUtc && new Date(s.lastSeenUtc) >= cutoff)).length;

    // Browser label using the same logic as the grid
    const counts = new Map<string, number>();
    for (const s of list) {
      const label = this.friendlyBrowser(s.chUa, s.userAgent);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }

    // Sort browsers by frequency desc, show top 4
    const browsers = Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);

    return { active, total, browsers };
  }


  private mapSortField(active: string): string {
    switch (active) {
      case 'emailAddress': return 'email';
      case 'firstName': return 'firstName';
      case 'lastName': return 'lastName';
      case 'dco': return 'dcoDisplay';
      case 'authSource': return 'authSource';
      case 'created': return 'createdUtc';
      case 'lastSeen': return 'lastSeenUtc';
      case 'isActive': return 'isTerminated';
      default: return 'createdUtc';
    }
  }
}
